import { useCallback, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';

import { useOrientation } from '../../boot/OrientationContext';
import { isApp } from '../../boot/utils';
import { white } from '../../constants/color';
import {
  GET_LAYOUT_MAX_WIDTH,
  GET_LIST_ITEM_NUM,
} from '../../constants/layout';
import { checkItemViewed } from '../../context/devLogFirehose';
import NoMoreItemsView from '../../elements/NoMoreItemsView';
import RefreshControl from '../../elements/RefreshControl';
import useScrollDirection from '../../hooks/useScrollDirection';
import { translate } from '../../i18n';
import { makeResultIdsWithFetching } from '../../utils/listWithPlaceholder';
import * as navigationService from '../../utils/navigationService';
import routeUtils from '../../utils/routes';
import { useRoute } from '../../utils/useRoute';
import FlatListWithImpression, {
  FlatListWithImpressionType,
} from '../FlatListWithImpression';
import { itemProducer } from '../FlatListWithImpression/utils';
import ListItem from '../ListItem';
import ProductListMeta from '../ProductListMeta';
import NotiBarContext from '../VendorItem/CollectBtn/NotiBarContext';
import { useData } from './hooks';
import VendorHeader from './VendorHeader';

interface Props {
  id: string;
}

const keyExtractor = item => `${item}`;

const LIST_PADDING = 10;
const ITEM_MARGIN = 5;
const LAYOUT_NAME = 'sellerpage';

export default ({ id }: Props) => {
  useOrientation();

  const impressionList = useRef<FlatListWithImpressionType>(null);

  const { params = {} } = useRoute();
  const currentTitle = params.title;
  const {
    sortBy,
    description,
    fetchData,
    sources,
    products,
    isFetching,
    currentPage,
    hasMoreItems,
    vendorName,
    headerBanner,
    eventBanner,
  } = useData(id);

  useEffect(() => {
    if (vendorName && currentTitle !== vendorName) {
      navigationService.refresh({ title: vendorName });
    }
  }, [currentTitle, vendorName]);

  const handleRefresh = useCallback(() => {
    const list = impressionList.current;

    if (typeof list?.clearImpressionAccumulatedItems === 'function') {
      list.clearImpressionAccumulatedItems();
    }

    fetchData({ type: 'reload', page: 1 });
  }, [fetchData]);

  const handleEndReached = useCallback(() => {
    if (!isFetching && hasMoreItems) {
      fetchData({ type: 'loadMore', page: currentPage + 1 });
    }
  }, [fetchData, isFetching, hasMoreItems, currentPage]);

  const renderItem = useCallback(({ item, index }) => {
    const containerWidth = GET_LAYOUT_MAX_WIDTH() - 2 * LIST_PADDING;

    return (
      <ListItem
        index={index}
        key={item}
        id={item}
        showLoading={`${item}`.includes('blank')}
        itemWidth={containerWidth / GET_LIST_ITEM_NUM()}
        itemMargin={ITEM_MARGIN}
        layoutName={LAYOUT_NAME}
        showLovesElement
        showSKUTag
        source={sources.get(item)}
        shouldDisplayImpressionForDebug={checkItemViewed(
          impressionList.current?.accumulatedItems,
          item
        )}
        linesOfTitle={1}
      />
    );
  }, []);

  const renderHeaderComponent = useCallback(() => {
    return (
      <VendorHeader
        sortBy={sortBy}
        description={description}
        id={id}
        vendorName={vendorName}
        headerBanner={headerBanner}
        eventBanner={eventBanner}
        isFetching={isFetching}
      />
    );
  }, [
    sortBy,
    description,
    id,
    vendorName,
    headerBanner,
    eventBanner,
    isFetching,
  ]);

  const renderFooter = useCallback(() => {
    if (hasMoreItems) {
      return null;
    }

    return (
      <NoMoreItemsView
        actionBtnText={translate('backToHomePage')}
        actionBtnWebProps={{
          accessibilityRole: 'link',
          href: routeUtils.compileToPath('Home'),
        }}
        onActionBtnPress={() => {
          navigationService.reset();
        }}
        useActionBtn
      />
    );
  }, [hasMoreItems]);

  const numColumns = GET_LIST_ITEM_NUM();

  const { onScroll, isScrollingDown, stopScrollListening } =
    useScrollDirection();
  const renderNotificationBar = useCallback(() => {
    if (!isApp) {
      stopScrollListening();

      return null;
    }

    return <NotiBarContext isDown={isScrollingDown} id={id} />;
  }, [id, isScrollingDown, stopScrollListening]);

  return (
    <>
      <ProductListMeta firstId={products[0]} />
      <FlatListWithImpression
        onScroll={onScroll}
        ListHeaderComponent={renderHeaderComponent}
        ListHeaderComponentStyle={styles.listHeaderComponentStyle}
        ref={impressionList}
        numColumns={numColumns}
        key={numColumns}
        columnWrapperStyle={styles.columnWrapperStyle}
        data={makeResultIdsWithFetching(products, isFetching)}
        renderItem={renderItem}
        onEndReached={handleEndReached}
        layoutName={LAYOUT_NAME}
        keyExtractor={keyExtractor}
        ListFooterComponent={renderFooter}
        dataProducer={itemProducer}
        sources={sources}
        refreshControl={
          <RefreshControl onRefresh={handleRefresh} isFetching={isFetching} />
        }
      />
      {renderNotificationBar()}
    </>
  );
};

const styles = StyleSheet.create({
  listHeaderComponentStyle: {
    zIndex: 1,
  },
  columnWrapperStyle: {
    backgroundColor: white,
    paddingHorizontal: LIST_PADDING,
  },
});
